import React, { useContext } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography, Divider, Fab, Card } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SyncIcon from '@material-ui/icons/Sync';

import { ConfigContext } from './config';
import CheckboxField from './Fields/Checkbox';
import NumberField from './Fields/Number';
import MinMaxField from './Fields/MinMax';
import ColorField from './Fields/Color';
import ShapesField from './Fields/Shapes';
import SliderField from './Fields/Slider';

import { ShapeContext } from './shapes';

const defaultExpanded = window.innerWidth > 800;

const Config = () => {
  const { useConfig } = useContext(ConfigContext);
  const { randomizeShapes } = useContext(ShapeContext);
  const kaleidoscopeEnabled = useConfig('kaleidoscope.enabled');
  const shadersEnabled = useConfig('shaders.enabled');
  const afterimageEnabled = useConfig('shaders.afterimage.enabled');
  const colorshiftEnabled = useConfig('shaders.colorshift.enabled');
  const bloomEnabled = useConfig('shaders.bloom.enabled');
  const bokehEnabled = useConfig('shaders.bokeh.enabled');

  return (
    <div className="controls">
      <Box flex={1} className="config">
        <Accordion defaultExpanded={defaultExpanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} id="controls-heading" aria-controls="controls-content">
            <Typography>Config</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%" mt={-1}>
              <Typography>Shapes</Typography>
              <Box display="flex">
                <NumberField id="shapes.count" label="Count" />
                <NumberField id="shapes.boundary" label="Boundary" />
              </Box>
              <Box mr={1}>
                <MinMaxField id="shapes.size" label="Size range" min={1} max={8} />
              </Box>
              <Box mb={1}>
                <ShapesField id="shapes.types" label="Shape types" />
              </Box>
              <Divider />
              <Box mt={2} mb={2}>
                <CheckboxField id="kaleidoscope.enabled" label="Kaleidoscope" />
                <fieldset disabled={!kaleidoscopeEnabled}>
                  <NumberField id="kaleidoscope.sides" label="Sides" disabled={!kaleidoscopeEnabled} />
                  <NumberField id="kaleidoscope.angle" label="Angle" disabled={!kaleidoscopeEnabled} />
                </fieldset>
              </Box>
              <Divider />
              <Box mt={2} display="flex" flexDirection="column">
                <CheckboxField id="enableAutoRotation" label="Auto-rotate" />
                <CheckboxField id="enableWireframes" label="Wireframes" />
                <ColorField id="backgroundColor" label="Background" />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        {shadersEnabled && (
          <>
            <Accordion>
              <AccordionSummary id="shaders-heading" aria-controls="shaders-content">
                <Typography>Shaders</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box mt={-2}>
                  <Typography variant="caption">
                    Declarative shaders and render effects are still kind of *waves hands* experimental
                  </Typography>
                  {/* <CheckboxField id="shaders.glitch" label="Glitch" /> */}
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="afterimage-controls-heading"
                aria-controls="afterimage-controls-content">
                <Typography>Afterimage</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%" mt={-1}>
                  <CheckboxField id="shaders.afterimage.enabled" label="Afterimage enabled" />
                  <SliderField
                    id="shaders.afterimage.damp"
                    label="Damp"
                    disabled={!afterimageEnabled}
                    step={0.01}
                    min={0.5}
                  />
                  <Box mt={1}>
                    <Typography variant="caption">
                      This turns off gamma correction which messes up the background color{' '}
                      <span role="img" aria-label="Upside down smile emoji">
                        🙃
                      </span>
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="afterimage-controls-heading"
                aria-controls="afterimage-controls-content">
                <Typography>Color shift</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%" mt={-1}>
                  <CheckboxField id="shaders.colorshift.enabled" label="Color shift enabled" />
                  <fieldset disabled={!colorshiftEnabled}>
                    <SliderField
                      id="shaders.colorshift.amount"
                      label="Amount"
                      max={0.25}
                      step={0.005}
                      disabled={!colorshiftEnabled}
                    />
                    <SliderField
                      id="shaders.colorshift.angle"
                      label="Angle (radians)"
                      max={3.14}
                      disabled={!colorshiftEnabled}
                    />
                  </fieldset>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="bloom-controls-heading"
                aria-controls="bloom-controls-content">
                <Typography>Bloom</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%" mt={-1}>
                  <CheckboxField id="shaders.bloom.enabled" label="Bloom enabled" />
                  <fieldset disabled={!bloomEnabled}>
                    <SliderField id="shaders.bloom.strength" label="Strength" disabled={!bloomEnabled} />
                    <SliderField id="shaders.bloom.radius" label="Radius" min={-1} disabled={!bloomEnabled} />
                    <SliderField id="shaders.bloom.threshold" label="Threshold" disabled={!bloomEnabled} />
                  </fieldset>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="bokeh-controls-heading"
                aria-controls="bokeh-controls-content">
                <Typography>Depth of field</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%" mt={-1}>
                  <CheckboxField id="shaders.bokeh.enabled" label="Depth of field enabled" />
                  <fieldset disabled={!bokehEnabled}>
                    <SliderField
                      id="shaders.bokeh.focus"
                      label="Focus"
                      disabled={!bokehEnabled}
                      min={-50}
                      max={50}
                      step={1}
                    />
                    <SliderField
                      id="shaders.bokeh.aperture"
                      label="Aperture"
                      disabled={!bokehEnabled}
                      max={2}
                      step={0.01}
                    />
                  </fieldset>
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Box>
      <Box pl={2}>
        <Fab onClick={randomizeShapes} size="medium" title="Randomize">
          <SyncIcon />
        </Fab>
      </Box>
    </div>
  );
};

export default Config;
